import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { NO_PREFRENCE } from '@constants/quiz';
import {
  getIsPendingAction,
  getQuizDetails,
  getSelectedLanguage
} from '@selectors/quizTaking';
import { setLanguage } from '@actions/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useCommonData } from '@stores/AppCommonData';
import { mediaQuery } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import { ProctorPermissionDialog } from '@cont/Quiz/Proctoring';
import { getIsQuizProctored } from '@cont/Quiz/utils';
import useProctorSessionFlow from '../../Common/useProctorSessionFlow';
import useTotalQuestions from '../../Common/useTotalQuestions';
import useQuizStatus from '../../Details/Common/useQuizStatus';
import NtaHeader, { NTA_HEADER_VARIANT } from '../components/NtaHeader';
import InstructionsBody from './InstructionsBody';
import InstructionsFooter from './InstructionsFooter';
import {
  NTA_COLORS,
  NTA_PAGE_MAX_WIDTH,
  NTA_PAGE_PADDING_X
} from '../constants';
import { ntaPageCss } from '../typography';
import { parseNtaStructuredPayload } from './ntaStructuredInstructions';

const Page = styled.div`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  min-height: 100vh;
  padding-bottom: 24px;
  background: ${NTA_COLORS.white};
  ${ntaPageCss}
`;

const PageContent = styled.div`
  width: 100%;
  max-width: ${NTA_PAGE_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 ${NTA_PAGE_PADDING_X};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mediaQuery.uptoMobile} {
    padding: 0 16px;
  }
`;
const NtaInstructionsPage = () => {
  const dispatch = useDispatch();
  const { query, pathname } = useRouter();
  const { quizUID, quizSlug } = query;
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const selectedLanguage = useSelector(getSelectedLanguage);
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);
  const totalQuestions = useTotalQuestions();
  const { isHeldInFuture, isTestCancelled } = useQuizStatus();
  const [, setStartTick] = useState(0);
  const { isProctored } = getIsQuizProctored(quizDetails);

  const hasTestStarted =
    !quizDetails?.start_time ||
    Date.now() >= new Date(quizDetails.start_time).getTime();

  const isTestStartDisabled =
    !totalQuestions || isTestCancelled || isHeldInFuture || !hasTestStarted;
  const currentPageInfo = useCommonData((state) => state.currentPageInfo);
  const setPageInfo = useCommonData((state) => state.setPageInfo);

  const languages = useMemo(
    () =>
      (quizDetails?.available_language ?? []).filter(
        ({ code }) => code !== NO_PREFRENCE.code
      ),
    [quizDetails]
  );

  const structuredInstructions = useMemo(
    () => parseNtaStructuredPayload(quizDetails?.instructions),
    [quizDetails?.instructions]
  );

  useEffect(() => {
    setPageInfo({
      ...currentPageInfo,
      layoutOptions: {
        ...currentPageInfo.layoutOptions,
        hideHeader: true,
        hideIntercom: true,
        hideFreshbot: true
      }
    });

    return () => {
      setPageInfo({
        ...currentPageInfo,
        layoutOptions: {
          ...currentPageInfo.layoutOptions,
          hideHeader: false
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!quizDetails?.start_time || hasTestStarted) return;

    const msUntilStart =
      new Date(quizDetails.start_time).getTime() - Date.now();

    if (msUntilStart <= 0) return;

    const timerId = setTimeout(
      () => setStartTick((tick) => tick + 1),
      msUntilStart
    );

    return () => clearTimeout(timerId);
  }, [hasTestStarted, quizDetails?.start_time]);

  useEffect(() => {
    const hasValidSelection =
      selectedLanguage?.code != null &&
      selectedLanguage.code !== NO_PREFRENCE.code;

    if (!hasValidSelection && languages[0]) {
      dispatch(setLanguage(languages[0]));
    }
  }, [dispatch, languages, selectedLanguage?.code]);

  const handleLanguageChange = (code) => {
    const language = languages.find(
      (lang) => String(lang.code) === String(code)
    );
    if (language) dispatch(setLanguage(language));
  };

  // Shallow push back to the same page. This keeps Layout/ProctorProvider
  // mounted so the SDK session survives - pushing to a separate route would
  // remount Layout and force the taking page to re-request permissions.
  const navigateToTaking = useCallback(
    () =>
      pushRoute(
        {
          pathname,
          query: {
            quizUID,
            quizSlug: quizSlug ?? quizDetails?.slug
          }
        },
        undefined,
        { shallow: true }
      ),
    [pathname, quizUID, quizSlug, quizDetails?.slug]
  );

  const {
    isEnrolling,
    showPermissionDialog,
    screenShareDenied,
    handleProceedClick,
    handlePermissionsGranted,
    handlePermissionsCancelled
  } = useProctorSessionFlow({ isProctored, onEnrolled: navigateToTaking });

  const handleProceed = async () => {
    if (isPendingAction || isEnrolling || isTestStartDisabled) return;
    await handleProceedClick();
  };

  return (
    <Page>
      <NtaHeader
        variant={NTA_HEADER_VARIANT.INSTRUCTIONS}
        languages={languages}
        selectedLanguage={selectedLanguage}
        onLanguageChange={handleLanguageChange}
      />
      <PageContent>
        <InstructionsBody instructionsHtml={quizDetails?.instructions} />
        <InstructionsFooter
          onProceed={handleProceed}
          isTestStartDisabled={isTestStartDisabled}
          isPendingAction={isPendingAction || isEnrolling}
          tncText={structuredInstructions?.tncText}
        />
      </PageContent>

      {/* Permission Dialog - acquires streams and stores them in ProctorProvider */}
      {showPermissionDialog && (
        <ProctorPermissionDialog
          onComplete={handlePermissionsGranted}
          onCancel={handlePermissionsCancelled}
          initialScreenPermissionState={screenShareDenied ? 'denied' : 'prompt'}
        />
      )}
    </Page>
  );
};

export default NtaInstructionsPage;
